import { NGXLogger } from 'ngx-logger';
import { scheduleOrphanedOverlayMaskSweep } from "src/app/commons/utils/overlay-mask-sweep";
import { Component, Inject, Injector, OnInit, HostListener, ViewChild, Input, AfterViewInit, OnDestroy, ChangeDetectorRef, NgZone, inject, effect } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { FontSizeServiceService } from "src/app/services/font-size-service.service";
import { SideBarMenuSharedService } from "src/app/services/side-bar-menu-shared.service";
import { DOCUMENT } from "@angular/common";
import { ThemeService, SYSTEM_THEME_VALUE } from "src/app/services/theme.service";
import { ToggleButtonModule } from "primeng/togglebutton";
import { UtilService } from "src/app/services/util.service";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { Menu } from "primeng/menu";
import { ToastMessagesService } from "src/app/commons/services/toast-messages.service";
import { LogoutService } from "src/app/services/logout.service";
import { Subject, Subscription, filter, takeUntil } from "rxjs";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { UserService } from "src/app/services/user.service";
import { CurrentUser } from "src/app/interfaces/current-user";
import { Location } from "@angular/common";
import { SecretKeyService } from "src/app/services/secret-key.service";
import { AppConstants } from "src/app/commons/app-constants";
import { Theme } from "src/app/commons/theme-constants";
import { SideBarComponent } from "../side-bar/side-bar.component";
import { SideBarMenuService } from "src/app/services/side-bar-menu.service";
import { SHARED_MODULES } from "../../shared-imports";
import { AppLanguage } from "src/app/i18n/translations";
import { TranslationService } from "src/app/i18n/translation.service";
import { TourService } from "src/app/services/tour/tour.service";
import { TranslatePipe } from 'src/app/i18n/translate.pipe';
import { BetaService } from "src/app/services/beta/beta.service";
import { NetworkStatusService } from "src/app/services/network-status.service";
import { isOfflineCapableNative } from "src/app/commons/utils/platform";
import { OutboxService } from "src/app/offline/outbox.service";
import { SyncEngineService } from "src/app/offline/sync-engine.service";
import { OfflinePrefetchService } from "src/app/offline/offline-prefetch.service";
import { CompanyMembershipService, CompanyMembership } from "src/app/services/company-membership.service";
import { PermissionService } from "src/app/services/permission.service";
import { NotificationBellComponent } from "src/app/notifications/notification-bell/notification-bell.component";
import { NotificationService } from "src/app/notifications/notification.service";
import { UnifiedSearchComponent } from "src/app/commons/unified-search/unified-search.component";
import { KeyboardShortcutsComponent } from "src/app/commons/keyboard-shortcuts/keyboard-shortcuts.component";
import { App } from "@capacitor/app";
import { FeedbackService } from "src/app/services/feedback/feedback.service";
import { MenuStyleService } from "src/app/services/menu-style.service";
import { AiChatWidgetSharedService } from "src/app/services/ai-chat-widget-shared.service";
import { isFrameScrollEvent, isShellLayout, pageScrollTop } from "src/app/commons/utils/page-scroll";

@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.css"],
    providers: [ToastMessagesService, MessageService],
    imports: [SHARED_MODULES, TranslatePipe, NotificationBellComponent, UnifiedSearchComponent, KeyboardShortcutsComponent]
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);
  private readonly feedbackService = inject(FeedbackService);
  private readonly injector = inject(Injector);
  /** Dock / classic menu choice, Focus mode (header hidden), where the header's menu button shows. */
  readonly menuStyle = inject(MenuStyleService);
  private readonly aiChatShared = inject(AiChatWidgetSharedService);
  /** "N not synced / N waiting to sync" chip — phone app only (OFFLINE_PLAN.md 1.9). */
  readonly outbox = inject(OutboxService);
  readonly isNativeApp = isOfflineCapableNative();
  private readonly syncEngine = inject(SyncEngineService);
  private readonly offlinePrefetch = inject(OfflinePrefetchService);
  /** "Sync now" is running (Phase 2). */
  private syncRefreshing = false;

  syncBusy(): boolean {
    return this.syncRefreshing || this.syncEngine.syncingCount() > 0;
  }

  syncIcon(): string {
    if (this.syncBusy()) return 'pi-sync pi-spin';
    if (this.outbox.refusedCount() > 0) return 'pi-exclamation-triangle';
    if (this.outbox.waitingCount() > 0) return 'pi-cloud-upload';
    return 'pi-cloud';
  }

  /** "Last synced 5 min ago" — last completed sync or offline refresh on this phone. */
  lastSyncedText(): string {
    const at = this.syncEngine.lastSyncAt();
    if (!at) return this.translationService.instant('offline.lastSyncedNever');
    const mins = Math.max(0, Math.floor((Date.now() - at) / 60000));
    let when: string;
    if (mins < 1) when = this.translationService.instant('offline.justNow');
    else if (mins < 60) when = this.translationService.instant('offline.minAgo').replace('{n}', String(mins));
    else if (mins < 60 * 24) when = this.translationService.instant('offline.hoursAgo').replace('{n}', String(Math.floor(mins / 60)));
    else when = this.translationService.instant('offline.daysAgo').replace('{n}', String(Math.floor(mins / 1440)));
    return this.translationService.instant('offline.lastSynced').replace('{when}', when);
  }

  /** Send what is waiting, then refresh the phone's offline copy. */
  async syncNow(): Promise<void> {
    if (this.syncBusy() || !this.networkStatusService.isOnline) return;
    this.syncRefreshing = true;
    try {
      await this.offlinePrefetch.refreshNow();
    } finally {
      this.syncRefreshing = false;
      this.outbox.refreshUnsyncedCount();
    }
  }
  // @ViewChild('avatar', { read: ElementRef }) avatar!: ElementRef;
  @ViewChild("menu") menu?: Menu;
  @Input() visibilityCondition: any;
  @Input() showLogo: boolean = false;
  @Input() showFontSize: boolean = false;
  @Input() showMenuButton: boolean = false;
  showMenuButton$ = this.sideBarMenuService.showMenuButton$;

  @Input() showUserProfileButton: boolean = false;
  @Input() showDayNightScheme: boolean = false;
  @Input() showHeader: boolean = false;

  isAvatarClickable = true;

  isContextMenuInitialized = false;
  @ViewChild(SideBarComponent) sidebarComponent!: SideBarComponent;
  userProfile: any;
  // Object-URL for the offline-cached avatar blob (see SideBarMenuService.cacheAvatarBlob) —
  // revoked in ngOnDestroy.
  private offlineAvatarUrl: string | null = null;
  // Real bug fixed 2026-08-26: the live profilePicture is a B2 signed URL that expires after
  // 4 hours (see B2SignedUrlService), but was cached in localStorage with no freshness check
  // and only ever re-fetched once per app launch — a backgrounded Capacitor WebView doesn't
  // restart, so a stale/expired URL could sit there for a whole session. Compounded by
  // networkStatusService.isOnline being unreliable on Android WebView (can report true with
  // no real connectivity), which could keep picking the (possibly-expired) live URL over the
  // offline-cached blob that would have worked. Set true when the <img> actually fails to
  // load; getAvatarImgSrc() then falls through to the offline-cached blob (or initials if
  // that's unavailable too) regardless of what isOnline claims. Reset whenever a fresh
  // profilePicture value arrives, so a genuinely new URL gets its own fair chance.
  avatarLoadFailed = false;
  onAvatarImageError(): void {
    this.avatarLoadFailed = true;
  }
  readonly defaultCompanyLogoUrl =
    "assets/images/logo.png";
  currentUser: CurrentUser | null = null;
  // Multi-company membership — populated once in ngAfterViewInit; empty/disabled unless
  // app.feature.multiCompanyMembership is on for this user's session.
  memberships: CompanyMembership[] = [];
  multiCompanyEnabled = false;
  private subscription: Subscription | null = null;
  private readonly _destroy$ = new Subject<void>();
  // Current user's own live-linked role name (null for Owner/Admin or RBAC-off tenants) —
  // shown as a plain info line in the avatar menu, same convention as the Company ID line.
  myAccessRoleName: string | null = null;

  isDarkThemeEnabled = false;
  isMobile!: boolean;

  avatarClickable$ = this.sideBarMenuService.avatarClickable$;

  showMenuBar = false;
  fontSizes = [
    { label: "XS", value: 0.75 },
    { label: "SM", value: 0.875 },
    { label: "MD", value: 1 },
    { label: "LG", value: 1.125 },
    { label: "XL", value: 1.25 },
    { label: "2XL", value: 1.5 },
  ];
  selectedFontSize: any;

  availableThemes = [
    { label: "System (Auto)", value: SYSTEM_THEME_VALUE, dark: null },
    { label: "Light",         value: Theme.Light,         dark: false },
    { label: "Dark",          value: Theme.Dark,          dark: true },
  ];
  selectedTheme: string = SYSTEM_THEME_VALUE;
  languages = this.translationService.languages;
  selectedLanguage: AppLanguage = this.translationService.currentLanguage;
  items!: MenuItem[];

  // ── 2026-10-07: the new "glass" header look ────────────────────────────────────────────────────────────────────────────
  // Pure look on top of the same controls. "Classic" is the earlier look, kept so it can be brought back with one click
  // (avatar menu -> "Use classic header"); the choice is remembered in this browser only.
  glassHeader = HeaderComponent.readHeaderStyle() !== 'classic';
  /** True once the page is scrolled down a little: the bar gets slimmer (glass look only). */
  slim = false;
  /** "+ New" entries the user may use (published by the sidebar, same list as the sidebar's own "+ New"). */
  newEntries: { labelKey: string; icon: string; link: string }[] = [];
  newItems: MenuItem[] = [];
  @ViewChild('newMenu') newMenu?: Menu;
  @ViewChild('usearch') usearch?: UnifiedSearchComponent;

  private static readHeaderStyle(): string {
    try { return localStorage.getItem('llHeaderStyle') || 'glass'; } catch { return 'glass'; }
  }

  private readonly onWindowScroll = (e?: Event): void => {
    // A list or table scrolling inside the page is not the page. (Only the app frame's own scroller counts, and only while the frame is on.)
    if (e && e.target instanceof Element && !(isShellLayout() && isFrameScrollEvent(e))) return;
    // Two thresholds so the bar does not flicker when its own height change shifts the scroll position.
    const y = isShellLayout() ? pageScrollTop() : (window.scrollY || document.documentElement.scrollTop || 0);
    const next = this.slim ? y > 8 : y > 32;
    if (next === this.slim) return;
    this.ngZone.run(() => {
      this.slim = next;
      this.syncHeaderHeightVar();
      this.cdr.markForCheck();
    });
  };

  /** Tells the pages that dock under the header how tall it is right now (glass look only; the classic look sets nothing and they keep their own 56px default). */
  private syncHeaderHeightVar(): void {
    const root = document.documentElement.style;
    if (this.menuStyle.headerHidden()) { root.setProperty('--app-hdr-h', '20px'); return; } // header collapsed: only the thin restore strip is left
    if (!this.glassHeader) { root.removeProperty('--app-hdr-h'); return; }
    const wide = window.innerWidth >= 768;
    root.setProperty('--app-hdr-h', (wide ? (this.slim ? 48 : 72) : (this.slim ? 44 : 56)) + 'px');
  }

  openNewMenu(event: Event): void {
    this.newItems = this.newEntries.map((e) => ({
      label: this.translationService.instant(e.labelKey),
      icon: e.icon,
      command: () => { this.router.navigateByUrl(e.link); },
    }));
    this.newMenu?.toggle(event);
  }

  /** The search pill is shown only when the search dialog would open (same two rights as UnifiedSearchComponent.canUse). */
  get canSearch(): boolean {
    return this.permissionService.hasEffective('GLOBAL_SEARCH_ACCESS') || this.permissionService.hasEffective('QUICK_SEARCH_ACCESS');
  }

  get canCreate(): boolean {
    return this.newEntries.length > 0;
  }

  openSearchFromPill(): void {
    this.usearch?.open();
  }

  /** Avatar-menu switch between the new look and the classic one. */
  private switchHeaderStyle(): void {
    this.glassHeader = !this.glassHeader;
    if (!this.glassHeader) this.slim = false;
    try { localStorage.setItem('llHeaderStyle', this.glassHeader ? 'glass' : 'classic'); } catch { /* storage blocked: applies for this visit only */ }
    this.syncHeaderHeightVar();
    this.initMenu();
    this.cdr.markForCheck();
  }

  showFullHeader = true;
  isThisMobile = false;
  prefPanelVisible = false;

  // Real gap found 2026-07-27: the main sidebar drawer already self-heals from an orphaned
  // overlay mask (interrupted close animation leaving a click-swallowing div behind — see
  // overlay-mask-sweep.ts) via this same sweep in its own onHide(), but this drawer never
  // called it at all, so the same failure here had no safety net and could stay stuck
  // indefinitely instead of clearing within 800ms.
  onPrefPanelHide(): void {
    scheduleOrphanedOverlayMaskSweep();
  }

  get hasSeenTour(): boolean {
    return this.tourService.hasSeenTour();
  }

  /** Tour "?" button: hidden while product tours are switched off (SUPER_ADMIN/ROOT still see it). */
  get showTourButton(): boolean {
    return this.tourService.replayAllowed();
  }

  readonly themeColorMap: Record<string, string> = {
    [SYSTEM_THEME_VALUE]: 'linear-gradient(135deg, #f1f5f9 50%, #1e293b 50%)',
    [Theme.Light]:         '#1684ac',
    [Theme.Dark]:          '#1684ac',
  };

  get selectedThemeColor(): string {
    return this.themeColorMap[this.selectedTheme] ?? '#1684ac';
  }

  getThemeColor(themeValue: string): string {
    return this.themeColorMap[themeValue] ?? '#1684ac';
  }

  constructor(
    private sideBarMenuSharedService: SideBarMenuSharedService,
    private router: Router,
    private fontSizeService: FontSizeServiceService,
    private themeService: ThemeService,
    private logOutService: LogoutService,
    private userService: UserService,
    private location: Location,
    private cdr: ChangeDetectorRef,
    private toastMessageService: ToastMessagesService,
    private utilService: UtilService,
    private sideBarMenuService: SideBarMenuService,
    private translationService: TranslationService,
    private tourService: TourService,
    private confirmationService: ConfirmationService,
    public betaService: BetaService,
    public networkStatusService: NetworkStatusService,
    private companyMembershipService: CompanyMembershipService,
    private permissionService: PermissionService,
    private notificationService: NotificationService,
    private http: HttpClient,
  ) {
    this.isDarkThemeEnabled = this.themeService.isDark;

    // console.log(`this.router.url ${this.location.path().includes('login')}`);
    this.onResize();
    this.hideHeaderOnLoginPage();

    const url = this.router.url;
    if (!url.includes("public")) {
      this.showMenuBar = true;
    }

    this.isMobile = window.innerWidth < 768; // adjust the breakpoint as needed

    // initMenu() (which bakes in the switch-company/Edit-Profile offline-disable state) was
    // otherwise only ever rebuilt by unrelated triggers (profile load, permission change,
    // membership refresh) — never by connectivity itself changing. A user who loads the app
    // online then goes offline would see a stale "still enabled" menu until one of those other
    // triggers happened to fire. Reacting to the raw online() signal (not the debounced
    // onlineTransitionTick()) since this just reflects current state, not a one-shot action.
    effect(() => {
      this.networkStatusService.online();
      this.tourService.replayAllowed(); // rebuild when tours are switched on/off (Tour menu item)
      this.initMenu();
    });
    // Going offline → pick up the latest saved profile picture (2026-09-29).
    effect(() => {
      if (!this.networkStatusService.online()) this.refreshOfflineAvatar();
    });
  }

  /** Re-reads the profile picture saved for offline use (see SideBarMenuService.avatarCached$). */
  private refreshOfflineAvatar(): void {
    this.sideBarMenuService.getCachedAvatarUrl().then((url) => {
      if (!url) return;
      if (this.offlineAvatarUrl) URL.revokeObjectURL(this.offlineAvatarUrl);
      this.offlineAvatarUrl = url;
    }).catch(() => undefined);
  }

  ngAfterViewInit(): void {
    this.sideBarMenuService.getDecryptedUserProfile().then((profile) => {
      this.userProfile = profile;
      this.logger.debug("Header cached profile loaded", {
        hasProfile: !!profile,
        profilePicture: profile?.profilePicture,
        companyLogoUrl: profile?.companyLogoUrl,
      });
      // Use the auth cookie as the authenticated signal rather than AppConstants.USER
      // (localStorage) — nothing in the live guard chain (AuthSessionGuard) populates that key at all,
      // it's written only by the old encrypted-blob login flow (deleted AppGuardGuard) which was
      // never actually wired into routing. The auth cookie, set by the backend before the
      // post-login redirect follows, is the reliable signal instead. Using it avoids skipping
      // getUserProfile() on first load, which caused the avatar to stay "U" until the next F5.
      const authCookiePresent = document.cookie
        .split('; ')
        .some(r => r.startsWith(AppConstants.ENCRYPTION_KEY_COOKIE_NAME + '='));
      if (authCookiePresent) {
        this.getUserProfile();
        this.loadMemberships();
      }
      this.updateFontSizeFromStorage();
      this.updateThemeFromStorage();
      this.subscribeToCurrentUser();
    });

    // The offline copy of the profile picture is re-read once it is saved and whenever the phone goes
    // offline (2026-09-29) — reading it only once here missed it on a fresh start (saved a moment later).
    this.refreshOfflineAvatar();
    this.sideBarMenuService.avatarCached$.pipe(takeUntil(this._destroy$)).subscribe(() => this.refreshOfflineAvatar());

    this.sideBarMenuService.avatarUpdated$.pipe(takeUntil(this._destroy$)).subscribe((profilePicture) => {
      if (this.userProfile) {
        this.userProfile = { ...this.userProfile, profilePicture };
        this.avatarLoadFailed = false;
        this.sideBarMenuService.setEncryptedUserProfile(this.userProfile);
      }
    });

    // Real bug fixed 2026-08-26: the signed avatar URL expires after 4 hours (see
    // B2SignedUrlService) but the profile was only ever fetched once per app launch — a
    // backgrounded Capacitor WebView doesn't restart, so a session left running across a
    // background/foreground cycle could sit on a stale/expired URL until a full app restart
    // forced a fresh fetch. Re-fetching on every resume keeps it current without needing that.
    if (isOfflineCapableNative()) {
      App.addListener('resume', () => {
        this.ngZone.run(() => this.getUserProfile());
      });
    }

    this.sideBarMenuService.companyLogoUpdated$.pipe(takeUntil(this._destroy$)).subscribe((companyLogoUrl) => {
      if (this.userProfile) {
        this.userProfile = { ...this.userProfile, companyLogoUrl };
        this.sideBarMenuService.setEncryptedUserProfile(this.userProfile);
      }
    });

    // Permissions (and this user's live-linked role name) load asynchronously and may resolve
    // after the menu items above are first built — re-render the avatar menu whenever it changes.
    this.permissionService.myAccessRoleName$.pipe(takeUntil(this._destroy$)).subscribe((roleName) => {
      this.myAccessRoleName = roleName;
      this.initMenu();
    });

    // Memberships were otherwise only ever loaded once, at bootstrap — accepting an invite
    // in-app (no fresh login) never refreshed the switcher, so the newly joined company stayed
    // invisible until a manual reload. Reuses the same real-time signal the notification bell
    // already fires (including right after the accept call itself succeeds, in this same tab).
    this.notificationService.refresh$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.loadMemberships();
    });

    // SideBarComponent already listens for this same signal to reload its own menu — added here
    // too (2026-09-06) so a refresh also re-fetches the profile this "Company ID:" row is built
    // from. Real bug confirmed live: a brand-new user's very first dashboard visit showed a blank
    // "Company ID:" because the avatar menu got built before the profile fetch had resolved, and
    // nothing here was listening for a later correction.
    this.sideBarMenuService.refreshMenu$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.getUserProfile();
    });
  }

  private getUserProfile() {
    // With retry (2026-09-30): the first profile load right after login / signup used to give up silently on one
    // failure and leave the avatar initial and "Company ID: -" until the next app start.
    this.sideBarMenuService.getUserProfileWithRetry().subscribe(
      (responseUserProfile: any) => {
        this.logger.debug(
          `In getUserProfile data ${JSON.stringify(responseUserProfile)}`,
        );
        this.userProfile = responseUserProfile;
        // Fresh profilePicture (fresh signed URL) — give it its own chance to load rather
        // than staying stuck on the offline fallback from a previous failure.
        this.avatarLoadFailed = false;
        this.logger.debug("Header fresh profile loaded", {
          profilePicture: responseUserProfile?.profilePicture,
          companyLogoUrl: responseUserProfile?.companyLogoUrl,
          companyId: responseUserProfile?.companyId,
        });
        this.initMenu();
        this.sideBarMenuService
          .setEncryptedUserProfile(responseUserProfile)
          .then(() => {
            //  console.log('User profile encrypted and set successfully');
          })
          .catch((error: any) => {
            this.logger.error("Failed to set encrypted user profile:", error);
          });
      },
      (error: any) => {},
      () => {},
    );
  }

  private loadMemberships(): void {
    this.companyMembershipService.getMyMemberships().subscribe({
      next: (res) => {
        this.multiCompanyEnabled = res.enabled;
        this.memberships = res.memberships || [];
        this.initMenu();
      },
      error: () => {
        // Feature disabled / not reachable — header behaves exactly as before (no switcher).
        this.multiCompanyEnabled = false;
        this.memberships = [];
      },
    });
  }

  async switchToCompany(companyId: number): Promise<void> {
    const target = this.memberships.find(m => m.companyId === companyId);
    if (!target || target.isActive) {
      return; // already active (or unknown) — no-op
    }
    // Checked here, at the moment of the action, rather than when the menu was built — a menu
    // assembled before a sync finished would otherwise stay stale. See
    // CompanyMembershipService.hasUnsyncedWork for why switching with queued writes is unsafe.
    if (await this.companyMembershipService.hasUnsyncedWork()) {
      this.toastMessageService.showErrorMessage(
        this.translationService.instant('common.switchCompany'),
        this.translationService.instant('common.switchCompanyBlockedUnsynced'),
      );
      return;
    }
    const message = this.translationService
      .instant('common.switchCompanyConfirmMessage')
      .replace('{{companyName}}', this.companyMembershipService.getDisambiguatedName(this.memberships, target));
    this.confirmationService.confirm({
      key: 'switchCompanyConfirm',
      message,
      header: this.translationService.instant('common.confirmSwitchCompany'),
      icon: 'pi pi-building',
      accept: () => this.performCompanySwitch(companyId),
    });
  }

  leaveActiveCompany(companyId: number, companyName: string): void {
    const displayName = this.companyMembershipService.getDisambiguatedName(
      this.memberships, this.memberships.find(m => m.companyId === companyId)!,
    ) || companyName;
    this.confirmationService.confirm({
      key: 'switchCompanyConfirm',
      message: this.translationService.instant('common.leaveCompanyConfirmMessage').replace('{{companyName}}', displayName),
      header: this.translationService.instant('common.leaveCompany'),
      icon: 'pi pi-sign-out',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.performLeaveCompany(companyId),
    });
  }

  private performLeaveCompany(companyId: number): void {
    this.companyMembershipService.leaveCompany(companyId).subscribe({
      next: () => {
        // The user can't stay "in" a company they just left — same full-reload pattern as
        // switching, landing them back on their own default company via the normal boot flow.
        window.location.href = '/';
      },
      error: (error: HttpErrorResponse) => {
        this.toastMessageService.showErrorMessage(
          this.translationService.instant('common.error'),
          error?.error?.message || this.translationService.instant('common.somethingWentWrong'),
        );
      },
    });
  }

  private performCompanySwitch(companyId: number): void {
    this.companyMembershipService.switchCompany(companyId).subscribe({
      next: () => {
        // Full reload — safest way to rehydrate every globally-cached piece of state
        // (menu, profile, plan limits, etc.) under the newly-issued session token.
        window.location.href = '/';
      },
      error: (error: HttpErrorResponse) => {
        this.toastMessageService.showErrorMessage(
          this.translationService.instant('common.error'),
          error?.error?.error || this.translationService.instant('common.somethingWentWrong'),
        );
      },
    });
  }

  private updateFontSizeFromStorage(): void {
    const storedFontSize = localStorage.getItem(AppConstants.FONT_SIZE);

    if (storedFontSize) {
      const storedFontSizeNumber = parseFloat(storedFontSize);
      const index = this.fontSizes.findIndex(
        (item) => item.value === storedFontSizeNumber,
      );
      if (index !== -1) {
        this.selectedFontSize = this.fontSizes[index];
      }
      // console.log(index);
    } else {
      this.fontSizeService.changeFontSize(1);
      document.documentElement.style.fontSize = 1 + "rem";
      this.selectedFontSize = this.fontSizes[2];
    }
  }

  private updateThemeFromStorage(): void {
    const storedTheme = localStorage.getItem(AppConstants.THEME);
    if (!storedTheme || storedTheme === SYSTEM_THEME_VALUE) {
      // Auto mode — theme already applied by ThemeService constructor
      this.selectedTheme = SYSTEM_THEME_VALUE;
      this.isDarkThemeEnabled = this.themeService.isDark;
    } else {
      const index = this.availableThemes.findIndex(item => item.value === storedTheme);
      this.selectedTheme = index !== -1 ? this.availableThemes[index].value : Theme.Light;
      this.isDarkThemeEnabled = this.DARK_THEMES.has(this.selectedTheme as Theme);
    }
    // Keep isDarkThemeEnabled in sync when OS preference changes (auto mode only)
    this.themeService.isDark$.pipe(takeUntil(this._destroy$)).subscribe(isDark => {
      if (this.selectedTheme === SYSTEM_THEME_VALUE) {
        this.isDarkThemeEnabled = isDark;
        this.cdr.detectChanges();
      }
    });
  }

  private subscribeToCurrentUser(): void {
    this.subscription = this.userService.currentUser$.subscribe(
      (currentUser) => {
        this.currentUser = currentUser;
        this.initMenu();
        this.preloadImage();
        // Ensure that the change detection is run after all state updates
        setTimeout(() => {
          this.cdr.detectChanges();
          this.isContextMenuInitialized = true;
        }, 1000);
      },
    );
  }

  private readonly TOUR_EXCLUDED_PREFIXES = [
    '/login', '/registration', '/account-setup', '/onboarding',
    '/email-verification', '/forgot-password', '/password-reset',
    '/user-added-to-existing-company',
  ];

  ngOnInit(): void {
    this.initMenu();
    // Menu style / icon set rows live in the avatar menu; Focus mode changes the header's own height (to nothing).
    effect(() => {
      this.menuStyle.style();
      this.menuStyle.railDesktop(); // the "Hide the menu" row shows only while the side rail is the menu
      this.menuStyle.headerHidden();
      this.aiChatShared.enabled(); // "Ask Linc" row shows only while Linc is on
      this.initMenu();
      this.syncHeaderHeightVar();
    }, { injector: this.injector });
    this.sideBarMenuService.newEntries$.pipe(takeUntil(this._destroy$)).subscribe((entries) => {
      this.newEntries = entries;
      this.cdr.markForCheck();
    });
    // One passive scroll check, outside Angular's change detection; nothing is attached to touch or wheel.
    this.syncHeaderHeightVar();
    this.ngZone.runOutsideAngular(() => window.addEventListener('scroll', this.onWindowScroll, { passive: true, capture: true }));
    // 2026-09-15 (user request): the "Welcome to LumberLinq!" product tour no longer starts by
    // itself — it kept popping up (even over the login screen) and users found it irritating. It
    // now runs only when chosen from the menu (Product Tour → onTourClick / menu command).

    this.sideBarMenuService.avatarClickable$.subscribe((v) => {
      this.isAvatarClickable = v;
    });
  }

  onTourClick(): void {
    this.tourService.startTour(true);
  }

  showHideMenu() {
    this.sideBarMenuSharedService.isMenuExpanded.emit(true);
    if (this.sidebarComponent) {
      this.sidebarComponent.focusSearchInput();
    }
  }

  onFontSizeChange(event: any) {
    const size: number =
      typeof event?.value === "object"
        ? (event.value?.value ?? 1)
        : (event?.value ?? 1);
    this.fontSizeService.changeFontSize(size);
    document.documentElement.style.fontSize = size + "rem";
    this.cdr.detectChanges();
  }

  toggleTheme() {
    // Toggling always exits auto/system mode and pins an explicit theme
    this.isDarkThemeEnabled = !this.isDarkThemeEnabled;
    this.selectedTheme = this.isDarkThemeEnabled ? Theme.Dark : Theme.Light;
    this.themeService.switchTheme(this.selectedTheme);
    this.cdr.detectChanges();
  }

  updateTheme() {
    this.selectedTheme = this.isDarkThemeEnabled ? Theme.Dark : Theme.Light;
    this.themeService.switchTheme(this.selectedTheme);
    localStorage.setItem(AppConstants.THEME, this.selectedTheme);
    this.cdr.detectChanges();
  }

  private readonly DARK_THEMES = new Set<Theme>([Theme.Dark]);

  onThemeChange(event: any) {
    const theme: string = event?.value ?? this.selectedTheme;
    this.selectedTheme = theme;
    if (theme === SYSTEM_THEME_VALUE) {
      this.isDarkThemeEnabled = this.themeService.isDark;
    } else {
      this.isDarkThemeEnabled = this.DARK_THEMES.has(theme as Theme);
    }
    this.themeService.switchTheme(theme); // handles persistence + auto mode
    this.cdr.detectChanges();
  }

  onLanguageChange(event: any) {
    this.selectedLanguage = event.value as AppLanguage;
    this.translationService.setLanguage(this.selectedLanguage);
    // Persist as the user's real default (not just this browser's localStorage) — same endpoint
    // the Profile page's language field uses, so both controls agree on one source of truth.
    // Fire-and-forget: localStorage already updated instantly above, so this failing silently
    // just means the change didn't follow them to another device, not a broken UI.
    this.http.put(`${AppConstants.API_URL}api/v1/profile/language`, { language: this.selectedLanguage }).subscribe({ error: () => {} });
    this.initMenu();
    this.cdr.detectChanges();
  }

  isSmallScreen = false;
  isMediumScreen = false;
  isLargeScreen = false;
  isExtraLargeScreen = false;

  private async initMenu() {
    const switchCompanyItems = this.companyMembershipService.buildSwitchCompanyMenuItems(
      this.memberships,
      this.translationService.instant('common.switchCompany'),
      (companyId) => this.switchToCompany(companyId),
    );
    // Only offered for the company currently being worked in, and only if it's not the user's
    // own default/home company — leaving their own company isn't this action's job (that's
    // self-delete, a much bigger decision with its own dedicated flow).
    const activeMembership = this.memberships.find(m => m.isActive);
    const canLeaveActiveCompany = !!activeMembership && !activeMembership.isDefault;
    this.items = [
      {
        label:
          "Company ID: " +
          (this.userProfile?.companyId ? this.userProfile.companyId : ""),
        styleClass: "text-sm",
        // Click-to-copy — no icon needed, the row itself is the affordance. Same method/toast
        // pattern as SideBarComponent.copyCompanyId() (its mobile-drawer equivalent), kept
        // consistent on purpose.
        command: () => this.copyCompanyId(),
      },
      // Only shown when this user is actually live-linked to a named AccessRole — null for
      // Owner/Admin (implicitly full access) and for RBAC-off tenants (no role concept applies),
      // so there's nothing meaningful to show in either of those cases.
      ...(this.myAccessRoleName
        ? [{
            label: "Role: " + this.myAccessRoleName,
            icon: "pi pi-fw pi-id-card",
            styleClass: "text-sm",
          }]
        : []),
      {
        separator: true,
      },
      ...switchCompanyItems,
      ...(canLeaveActiveCompany ? [{
        label: this.translationService.instant('common.leaveCompany'),
        icon: 'pi pi-sign-out',
        command: () => this.leaveActiveCompany(activeMembership!.companyId, activeMembership!.companyName),
      }] : []),
      ...(switchCompanyItems.length > 0 || canLeaveActiveCompany ? [{ separator: true }] : []),
      {
        // edit/account-details is already OfflineGuard-protected server-side; disabling here
        // too avoids the dead click -> redirect -> offline-unavailable round-trip. Same
        // disabled + label-suffix treatment as buildSwitchCompanyMenuItems (no native
        // tooltip/disabled support on a MenuItem the way a pButton has).
        label: this.translationService.instant('menu.editProfile') + (!this.networkStatusService.isOnline ? ` (${this.translationService.instant('offline.unavailableTitle')})` : ''),
        icon: "pi pi-fw pi-pencil",
        disabled: !this.networkStatusService.isOnline,
        command: () => {
          this.router.navigateByUrl("edit/account-details");
        },
      },
      {
        // "storage" is OfflineGuard-protected server-side — same disabled + label-suffix
        // treatment as "Edit Profile" just above, for the same reason.
        label: this.translationService.instant('menu.storage') + (!this.networkStatusService.isOnline ? ` (${this.translationService.instant('offline.unavailableTitle')})` : ''),
        icon: "pi pi-fw pi-database",
        disabled: !this.networkStatusService.isOnline,
        command: () => {
          this.router.navigateByUrl("storage");
        },
      },
      {
        label: "Product Tour",
        icon: "pi pi-fw pi-compass",
        visible: this.tourService.replayAllowed(),
        command: () => this.tourService.startTour(true),
      },
      {
        // the round Linc / Support button is hidden (2026-10-07): Linc AI help opens from here
        label: this.translationService.instant('menuDock.askLinc'),
        icon: "pi pi-fw pi-comment",
        visible: this.aiChatShared.enabled(),
        command: () => this.aiChatShared.requestOpen(),
      },
      {
        label: this.translationService.instant('appFeedback.menu'),
        icon: "pi pi-fw pi-star",
        command: () => this.feedbackService.openDialog(),
      },
      {
        label: this.translationService.instant(this.glassHeader ? 'header.useClassic' : 'header.useNew'),
        icon: "pi pi-fw pi-window-maximize",
        command: () => this.switchHeaderStyle(),
      },
      {
        label: this.translationService.instant(this.menuStyle.dockOn() ? 'menuDock.useClassic' : 'menuDock.useDock'),
        icon: "pi pi-fw pi-bars",
        command: () => this.menuStyle.toggleStyle(),
      },
      ...(this.menuStyle.railDesktop()
        ? [{
            label: this.translationService.instant('menuDock.railHide'),
            icon: "pi pi-fw pi-angle-double-left",
            command: () => this.menuStyle.setRailMode('hidden'),
          }]
        : []),
      {
        label: "Logout",
        icon: "pi pi-power-off",
        command: () => {
          this.confirmationService.confirm({
            key: 'headerLogoutConfirm',
            message: this.translationService.instant('auth.logoutConfirmMessage'),
            header: this.translationService.instant('auth.confirmLogout'),
            icon: 'pi pi-power-off',
            accept: () => {
              this.logOutService.logout().subscribe((isSuccess) => {
                this.logger.debug("Header logout result", { isSuccess });
                if (isSuccess) {
                  this.toastMessageService.showSuccessMessage(
                    this.translationService.instant('auth.logoutSuccess'),
                    this.translationService.instant('auth.logoutSuccessMsg'),
                    false,
                    "logout-toast",
                  );
                } else {
                  this.toastMessageService.showErrorMessage(
                    this.translationService.instant('auth.logoutFailed'),
                    this.translationService.instant('auth.logoutFailedMsg'),
                  );
                }
              });
            }
          });
        },
      },
      /* {
        label: 'Reset ',
        icon: 'pi pi-fw pi-pencil',
        command: () => {
          this.userService.resetDatabase().subscribe(
          (response:any)=>{
            console.log(`response of resetdatabase is ${JSON.stringify(response)}`);
          },
          (error:any)=>{
            console.log(`error of resetdatabase is ${JSON.stringify(error)}`);
          },
          ()=>{},
          )
        },
      } */
    ];
  }

  // Same method name/toast pattern as SideBarComponent.copyCompanyId() (mobile drawer's
  // equivalent) — kept consistent on purpose. Unlike that one, this row has no separate copy
  // icon; the p-menu item's own `command` above is the only trigger, so there's no risk of a
  // second toast firing for the same click.
  copyCompanyId(): void {
    const id = this.userProfile?.companyId;
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => {
      this.toastMessageService.showSuccessMessage('Copied!', `Company ID "${id}" copied to clipboard.`, false, 'header-copy-toast');
    }).catch(() => {
      this.toastMessageService.showErrorMessage('Copy failed', 'Could not copy to clipboard.');
    });
  }

  @HostListener("window:resize")
  onResize() {
    const screenWidth = window.innerWidth;

    this.isSmallScreen = screenWidth >= 576 && screenWidth < 768;
    this.isMediumScreen = screenWidth >= 768 && screenWidth < 992;
    this.isLargeScreen = screenWidth >= 992 && screenWidth < 1200;
    this.isExtraLargeScreen = screenWidth >= 1200;

    this.isThisMobile = this.utilService.isMobile();
    this.syncHeaderHeightVar();
  }

  toggleMenu(event: MouseEvent) {
    if (!this.menu) {
      this.logger.warn("Header :: context menu not yet initialized");
      return;
    }
    this.menu.toggle(event);
    event.stopPropagation();
  }

  private hideHeaderOnLoginPage() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        // Add the route paths for which you want to hide the components
        const hiddenRoutes = ["login"];

        const shouldHide = hiddenRoutes.some((route) => {
          //  console.log(`route ${route} == hiddenRoutes ${hiddenRoutes}`);
          event.urlAfterRedirects.startsWith(`/${route}`);
        });

        this.showFullHeader = false;
      });
  }
  imageIsValid: boolean = true; // Start with true by default

  preloadImage() {
    const img = new Image();
    const url = this.currentUser?.imageUrl ?? "";
    // console.log("Header Image URL:"+ url);

    img.src = url;

    img.onload = () => {
      this.imageIsValid = true;
      // console.log("Header Image :: -> Image loaded successfully");
    };

    img.onerror = (event: any) => {
      this.imageIsValid = false;
      //  console.log("Header Image :: -> Error loading image   "+JSON.stringify(event));
    };
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onWindowScroll, { capture: true });
    document.documentElement.style.removeProperty('--app-hdr-h');
    this._destroy$.next();
    this._destroy$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.offlineAvatarUrl) {
      URL.revokeObjectURL(this.offlineAvatarUrl);
    }
  }

  // Online, the live signed URL (or external OAuth avatar URL) renders directly. Offline (or
  // once the live URL has actually failed to load — see avatarLoadFailed/onAvatarImageError
  // above), falls back to whatever cacheAvatarBlob already cached, else undefined — the
  // template's *ngIf then falls through to the initials avatar instead of a broken-image icon.
  getAvatarImgSrc(): string | undefined {
    if (!this.avatarLoadFailed && (this.networkStatusService.isOnline || !isOfflineCapableNative())) {
      return this.userProfile?.profilePicture;
    }
    return this.offlineAvatarUrl ?? undefined;
  }

  /** The company logo failed to load (2026-09-28) — show the default LumberLinq logo instead of a broken
   *  image. Only clears a custom logo, so the default itself can never loop here. */
  onCompanyLogoError(): void {
    if (this.userProfile?.companyLogoUrl) {
      this.userProfile = { ...this.userProfile, companyLogoUrl: null };
    }
  }

  get companyLogoUrl(): string {
    const logoUrl = this.userProfile?.companyLogoUrl;
    return logoUrl || this.defaultCompanyLogoUrl;
  }
}
